import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function WorkOrderForm() {
  const [searchParams] = useSearchParams();
  const searchId = searchParams.get("search") || "";
  const candidateIds = searchParams.get("candidates")?.split(",").filter(Boolean) || [];
  const { api } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [searchTitle, setSearchTitle] = useState("");
  const [woType, setWoType] = useState("");
  const [priority, setPriority] = useState("standard");
  const [title, setTitle] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [applicationUrl, setApplicationUrl] = useState("");
  const [opportunityDesc, setOpportunityDesc] = useState("");
  const [companyTypeDesc, setCompanyTypeDesc] = useState("");
  const [highlights, setHighlights] = useState("");
  const [responseWindow, setResponseWindow] = useState(14);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [woId, setWoId] = useState("");

  useEffect(() => {
    if (searchId) {
      api.get(`/role-searches/${searchId}`).then(r => {
        setSearchTitle(r.data.title);
        setTitle(r.data.title);
        setJobDescription(r.data.role_description_text || "");
      }).catch(() => {});
    }
  }, [searchId, api]);

  const handleSubmit = async () => {
    if (!woType) { toast.error("Select a work order type"); return; }
    if (!title.trim()) { toast.error("Enter a title"); return; }
    setSubmitting(true);
    try {
      const payload = {
        search_id: searchId, type: woType, title, candidate_pool: candidateIds,
        priority, job_title: jobTitle, job_description_text: jobDescription,
        application_url: applicationUrl, opportunity_description: opportunityDesc,
        company_type_description: companyTypeDesc, opportunity_highlights: highlights,
        preferred_response_window_days: responseWindow, special_instructions: specialInstructions,
      };
      const res = await api.post("/work-orders", payload);
      setWoId(res.data.id);
      setSubmitted(true);
      toast.success("Work Order submitted");
    } catch (e) { toast.error("Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center animate-fadeIn" data-testid="wo-submitted">
        <div className="w-20 h-20 rounded-full bg-lime-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 text-lime-600" />
        </div>
        <h2 className="text-2xl font-heading text-slate-900 mb-2">Work Order Submitted</h2>
        <p className="text-xs font-mono text-slate-400 uppercase mb-4">{woId}</p>
        <Badge className="bg-amber-100 text-amber-700 border-0 text-sm mb-8">Pending Admin Review</Badge>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => navigate("/work-orders")} className="bg-evergreen-950 hover:bg-evergreen-900 text-white font-accent">View Work Orders</Button>
          <Button variant="outline" onClick={() => navigate("/")}>Back to Role Board</Button>
        </div>
      </div>
    );
  }

  const steps = [
    { num: 1, label: "Type & Candidates" },
    { num: 2, label: woType === "job_promotion" ? "Job Details" : "Outreach Details" },
    { num: 3, label: "Review & Submit" }
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="work-order-form">
      <h1 className="text-2xl font-heading text-slate-900 mb-6">New Work Order</h1>

      {/* Progress dots */}
      <div className="flex items-center gap-4 mb-8">
        {steps.map((s, idx) => (
          <div key={s.num} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-accent border-2 transition-colors ${
              step > s.num ? "bg-lime-600 border-lime-600 text-white" : step === s.num ? "bg-white border-evergreen-950 ring-4 ring-lime-100" : "bg-white border-slate-300 text-slate-400"
            }`}>{s.num}</div>
            <span className={`text-xs ${step >= s.num ? "text-slate-700" : "text-slate-400"}`}>{s.label}</span>
            {idx < steps.length - 1 && <div className="w-12 h-px bg-slate-200" />}
          </div>
        ))}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <Card className="animate-slideUp" data-testid="wo-step-1">
          <CardContent className="p-6 space-y-6">
            <div>
              <p className="text-sm text-slate-600 mb-2">{candidateIds.length} candidates selected from <strong>{searchTitle}</strong></p>
              <div className="flex flex-wrap gap-1.5">{candidateIds.map(id => <Badge key={id} variant="outline" className="font-mono text-xs">{id}</Badge>)}</div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Work Order Type</Label>
              <div className="grid grid-cols-2 gap-3">
                {[["job_promotion", "Job Promotion", "Promote a specific role to selected candidates"], ["deidentified_outreach", "De-Identified Outreach", "Gauge interest without revealing company identity"]].map(([val, label, desc]) => (
                  <button key={val} onClick={() => setWoType(val)} data-testid={`wo-type-${val}`}
                    className={`p-4 rounded-xl border-2 text-left transition-colors ${woType === val ? "border-lime-500 bg-lime-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <p className="font-accent font-medium text-slate-900 text-sm">{label}</p>
                    <p className="text-xs text-slate-500 mt-1">{desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Priority</Label>
              <div className="flex gap-2">
                {[["standard", "Standard"], ["urgent", "Urgent"]].map(([val, label]) => (
                  <button key={val} onClick={() => setPriority(val)}
                    className={`px-4 py-1.5 text-sm font-accent rounded-full border ${priority === val ? "bg-evergreen-950 text-white border-evergreen-950" : "bg-white text-slate-500 border-slate-200"}`}>{label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Title</Label>
              <Input data-testid="wo-title-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Work order reference name" />
            </div>
            <Button data-testid="wo-next-btn" onClick={() => { if (woType && title) setStep(2); else toast.error("Fill required fields"); }} className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white">
              Continue
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <Card className="animate-slideUp" data-testid="wo-step-2">
          <CardContent className="p-6 space-y-4">
            {woType === "job_promotion" ? (
              <>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Job Title</Label><Input value={jobTitle} onChange={e => setJobTitle(e.target.value)} /></div>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Job Description</Label><Textarea value={jobDescription} onChange={e => setJobDescription(e.target.value)} className="min-h-[120px]" /></div>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Application URL</Label><Input value={applicationUrl} onChange={e => setApplicationUrl(e.target.value)} placeholder="https://..." /></div>
              </>
            ) : (
              <>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Opportunity Description (De-identified)</Label><Textarea value={opportunityDesc} onChange={e => setOpportunityDesc(e.target.value)} className="min-h-[100px]" placeholder="Describe the opportunity without revealing your company..." /></div>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Company Type</Label><Input value={companyTypeDesc} onChange={e => setCompanyTypeDesc(e.target.value)} placeholder="e.g. Large pharma with oncology focus" /></div>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">What Makes This Compelling</Label><Textarea value={highlights} onChange={e => setHighlights(e.target.value)} /></div>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Response Window (Days)</Label><Input type="number" value={responseWindow} onChange={e => setResponseWindow(parseInt(e.target.value) || 14)} /></div>
              </>
            )}
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Special Instructions</Label><Textarea value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)} placeholder="Any additional notes..." /></div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button data-testid="wo-to-review-btn" onClick={() => setStep(3)} className="flex-1 bg-evergreen-950 hover:bg-evergreen-900 text-white">Review</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <Card className="animate-slideUp" data-testid="wo-step-3">
          <CardContent className="p-6 space-y-4">
            <h3 className="font-heading text-lg text-slate-900">Review Work Order</h3>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
              <p><span className="text-slate-500">Type:</span> <span className="font-medium">{woType === "job_promotion" ? "Job Promotion" : "De-Identified Outreach"}</span></p>
              <p><span className="text-slate-500">Title:</span> <span className="font-medium">{title}</span></p>
              <p><span className="text-slate-500">Priority:</span> <span className="font-medium capitalize">{priority}</span></p>
              <p><span className="text-slate-500">Candidates:</span> <span className="font-medium">{candidateIds.length}</span></p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button data-testid="wo-submit-btn" onClick={handleSubmit} disabled={submitting} className="flex-1 bg-evergreen-950 hover:bg-evergreen-900 text-white py-3">
                {submitting ? "Submitting..." : "Submit Work Order"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
